import { useFormikContext } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../../components/Button/Button';
import Input from '../../../../../../../components/Input/Input';
import { useAppSelector } from '../../../../../../../reduxService/hooks';
import { ISubSectorResponse } from '../../../../../api/IApiResponse';
import { SubSectorPosition } from '../../../../../enum/Sector';
import { selectProject } from '../../../../../selector/conversion';
import { getEmptySubsector } from '../../../../../utils/utils';
import FormikMaterial from '../../ModalMaterial/FormikMaterial';
import { IFormikFormSectors } from '../FormikSubsector';
import { EConversionSIToImperial, conversionLabel, convertTo } from '../../../../../../conversion';
import { roundToTwo } from '../../../Geometry/utils';
import { selectProjectInfo } from '../../../../../../store/projectInfo/projectInfoSlice';
import { UnitOfMeasure } from '../../../../../../enum/project';
import { isConcrete } from '../../../../../../enum/Material';

type Props = {};
interface ISubsLength {
    right: number;
    left: number;
    center: number;
}
export const SubsectorBox = (props: Props) => {
    const { t } = useTranslation();
    const { values } = useFormikContext<IFormikFormSectors>();

    return (
        <div className='d-flex justify-content-between gap-3 mb-3'>
            {values[values.choosenSector].subSectorNumber === SubSectorPosition.Right && (
                <SingleSubsector subSectorNumber={SubSectorPosition.Right} positionString={t('modal_subsector_main')} disableLength subSector={values[values.choosenSector][SubSectorPosition.Right]} />
            )}
            {values[values.choosenSector].subSectorNumber === SubSectorPosition.Left && (
                <>
                    <SingleSubsector positionString={t('left')} subSectorNumber={SubSectorPosition.Left} disableLength subSector={values[values.choosenSector][SubSectorPosition.Left]} />
                    <SingleSubsector positionString={t('right')} subSectorNumber={SubSectorPosition.Right} subSector={values[values.choosenSector][SubSectorPosition.Right]} />
                </>
            )}
            {values[values.choosenSector].subSectorNumber === SubSectorPosition.Center && (
                <>
                    <SingleSubsector positionString={t('left')} subSectorNumber={SubSectorPosition.Left} subSector={values[values.choosenSector][SubSectorPosition.Left]} />
                    <SingleSubsector positionString={t('center')} subSectorNumber={SubSectorPosition.Center} disableLength subSector={values[values.choosenSector][SubSectorPosition.Center]} />
                    <SingleSubsector positionString={t('right')} subSectorNumber={SubSectorPosition.Right} subSector={values[values.choosenSector][SubSectorPosition.Right]} />
                </>
            )}
        </div>
    );
};
interface ISingleSubsector {
    positionString: string;
    disableLength?: boolean;
    subSector: ISubSectorResponse;
    subSectorNumber: SubSectorPosition;
    setSubSectorsLength?: any;
}

const SingleSubsector = (props: ISingleSubsector) => {
    const { t } = useTranslation();
    const { values, setFieldValue } = useFormikContext<IFormikFormSectors>();
    const [openFormikMat, setOpenFormikMat] = useState<boolean>(false);
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const [subsector, setSubsector] = useState<ISubSectorResponse>(props.subSector || getEmptySubsector(props.subSectorNumber, values[values.choosenSector]));

    useEffect(() => {
        if (!!props.subSector && !!props.subSector.material) {
            setSubsector(props.subSector);
        } else {
            setSubsector(getEmptySubsector(props.subSectorNumber, values[values.choosenSector]));
        }
    }, [props.subSector]);

    const { subSectorNumber } = props;
    const updateMaterial = useCallback((data: any) => {
        setSubsector({ ...subsector, material: data });
        setFieldValue(`${values.choosenSector}.${subSectorNumber}.material`, { ...data });
    }, []);
    const maxLength = values[values.choosenSector].length;
    let stress = subsector.material?.isVegetated ? roundToTwo(subsector?.material?.physicalData![0].vegetatedAllowShearStress) : roundToTwo(subsector?.material?.physicalData![0].allowShearStress);

    if (isImperial) stress = roundToTwo(convertTo(stress, EConversionSIToImperial.Nm2_to_lbfft2));

    return (
        <div className='border border-2 p-1'>
            <b>{props.positionString}</b>
            <div className='d-flex flex-column gap-2'>
                <Input
                    type='number'
                    step={0.01}
                    title={t('modal_subsector_subsector_length', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                    name='subsectorLength'
                    disabled={props.disableLength}
                    value={subsector.length ? parseFloat(subsector.length?.toFixed(2)) : 0}
                    onChange={e => {
                        if (!props.disableLength) {
                            let value = parseFloat(e.target.value);
                            if (Number.isNaN(value)) {
                                value = 0;
                            }
                            if (!!maxLength) {
                                if (subSectorNumber === SubSectorPosition.Right && value < maxLength) {
                                    setFieldValue(`${values.choosenSector}.${subSectorNumber}`, { ...values[values.choosenSector][subSectorNumber], length: value });
                                    setFieldValue(`${values.choosenSector}.${SubSectorPosition.Left}`, { ...values[values.choosenSector][SubSectorPosition.Left], length: maxLength - value });
                                    setFieldValue(`${values.choosenSector}.${SubSectorPosition.Center}`, {
                                        ...values[values.choosenSector][SubSectorPosition.Center],
                                        length: 0,
                                    });
                                } else {
                                    setFieldValue(`${values.choosenSector}.${subSectorNumber}`, { ...values[values.choosenSector][subSectorNumber], length: value });
                                }
                                const leftV = maxLength - values[values.choosenSector][SubSectorPosition.Right].length!;

                                if (subSectorNumber === SubSectorPosition.Left && value > 0 && value < leftV) {
                                    setFieldValue(`${values.choosenSector}.${subSectorNumber}`, { ...values[values.choosenSector][subSectorNumber], length: value });
                                    setFieldValue(`${values.choosenSector}.${SubSectorPosition.Center}`, {
                                        ...values[values.choosenSector][SubSectorPosition.Center],
                                        length: maxLength - values[values.choosenSector][SubSectorPosition.Right].length! - value,
                                    });
                                } else {
                                    setFieldValue(`${values.choosenSector}.${subSectorNumber}`, { ...values[values.choosenSector][subSectorNumber], length: value });
                                }
                            }
                        }
                    }}
                />
                <div className='d-flex align-items-center gap-2'>
                    <Button
                        onClick={e => {
                            setOpenFormikMat(true);
                        }}
                        type='button'
                        classNameBtn='open-material-btn'>
                        <i className='fa-solid fa-pen-to-square' />
                    </Button>

                    <div style={{ backgroundColor: `${subsector?.material?.color}`, width: '15px', height: '15px' }}></div>
                    {subsector?.material?.name}
                </div>
                <Input
                    type='number'
                    step={0.0001}
                    title={t('material_roughness')}
                    name='roughness'
                    className='d-flex flex-column'
                    disabled
                    value={subsector.material?.isVegetated ? subsector?.material?.physicalData![0].vegetatedRoughness : subsector?.material?.physicalData![0].roughness}
                />
                {!isConcrete(subsector.material?.category) && (
                    <Input
                        type='number'
                        step={0.01}
                        title={t('material_shear_stress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })}
                        name='stress'
                        className='d-flex flex-column'
                        disabled
                        value={stress}
                    />
                )}
                {openFormikMat && (
                    <FormikMaterial
                        open={openFormikMat}
                        sectorChannel={values.choosenSector}
                        materialId={subsector.material?.id}
                        closeModal={() => setOpenFormikMat(false)}
                        onClose={data => updateMaterial(data)}
                        material={subsector.material}
                    />
                )}
            </div>
        </div>
    );
};
